import { UPLOAD_POLICY, formatFileSize, type FormField } from '@mapeamento/shared';
import { esc } from '../lib/html';
import type { AttachmentBag } from '../state/attachment-bag';
import type { DraftStore } from '../state/draft-store';

export interface FieldContext {
  store: DraftStore;
  attachments: AttachmentBag;
}

const ACCEPT = UPLOAD_POLICY.allowedExtensions.map((ext) => `.${ext}`).join(',');

/**
 * Renderiza um campo da definição compartilhada. O `data-path` do wrapper
 * é o mesmo caminho usado pelo schema Zod, o que permite mostrar os erros
 * da validação (local ou da API) no campo certo.
 */
export function renderField(field: FormField, path: string, ctx: FieldContext): string {
  const value = ctx.store.get(path);
  const id = `f_${path.replaceAll('.', '_')}`;
  const reqClass = field.required ? 'req' : '';
  const hint = field.hint ? `<p class="hint">${esc(field.hint)}</p>` : '';
  let html = `<div class="f" data-path="${path}" data-req="${field.required ? 1 : 0}" data-type="${field.type}">`;

  switch (field.type) {
    case 'text':
    case 'email':
    case 'tel':
      html += `<label class="${reqClass}" for="${id}">${esc(field.label)}</label>${hint}
        <input type="${field.type}" id="${id}" value="${esc(value)}" autocomplete="off">`;
      break;

    case 'textarea':
      html += `<label class="${reqClass}" for="${id}">${esc(field.label)}</label>${hint}
        <textarea id="${id}">${esc(value)}</textarea>`;
      break;

    case 'radio':
    case 'check': {
      const selected = field.type === 'check' ? ((value as string[] | undefined) ?? []) : [value];
      const inputType = field.type === 'check' ? 'checkbox' : 'radio';
      html += `<span class="lg ${reqClass}">${esc(field.label)}</span>${hint}<div class="opts">`;
      for (const option of field.options) {
        const checked = selected.includes(option) ? ' checked' : '';
        html += `<label class="opt"><input type="${inputType}" name="${id}" value="${esc(option)}"${checked}><span>${esc(option)}</span></label>`;
      }
      html += '</div>';
      break;
    }

    case 'file': {
      const items = ctx.attachments.list
        .map(
          (file, i) =>
            `<li><b>${esc(file.name)}</b><span>${formatFileSize(file.size)}<button type="button" data-rm="${i}" aria-label="Remover ${esc(file.name)}">✕</button></span></li>`,
        )
        .join('');
      const stale = ctx.store.hadStaleAttachments
        ? '<p class="notice">Os anexos escolhidos antes de recarregar a página precisam ser selecionados novamente.</p>'
        : '';
      html += `<span class="lg">${esc(field.label)}</span>${hint}${stale}
        <div class="drop" data-drop><input type="file" multiple accept="${ACCEPT}" aria-label="${esc(field.label)}">Arraste arquivos aqui ou clique para selecionar</div>
        <p class="hint drop-rules">Até ${UPLOAD_POLICY.maxFiles} arquivos de ${formatFileSize(UPLOAD_POLICY.maxFileSizeBytes)} cada.</p>
        <p class="em-files" role="alert"></p>
        <ul class="files">${items}</ul>`;
      break;
    }

    case 'repeat': {
      let items = value as Record<string, unknown>[] | undefined;
      if (!items?.length) {
        items = [{}];
        ctx.store.set(path, items);
      }
      html += `<div class="rep" data-rep="${path}">`;
      items.forEach((_, i) => {
        const removable = items.length > (field.min ?? 0);
        html += `<div class="rep-item"><div class="rh"><b>${esc(field.label)} ${i + 1}</b>${
          removable ? `<button type="button" data-del="${i}">Remover</button>` : ''
        }</div>`;
        for (const sub of field.fields) html += renderField(sub, `${path}.${i}.${sub.id}`, ctx);
        html += '</div>';
      });
      html += `<button type="button" class="rep-add" data-add>+ Adicionar ${esc(field.label.toLowerCase())}</button></div>`;
      break;
    }
  }

  return `${html}<p class="em">Campo obrigatório.</p></div>`;
}
